import { OcrField } from "../../../backend/taxguru/models/OcrField";
import _ from "lodash";
import { getPersonName, isValueDefined } from "@mm-products/java-multima-framework";
import { FieldPath, Path } from "react-hook-form";
import {
    documentDateObjectFields,
    DocumentDetailFormTypes,
    documentNumberFields,
    documentObjectFields,
    documentTaxFields,
} from "../detail/DocumentDetail.types";
import { RecogniserResponse } from "../../../backend/taxguru/models/RecogniserResponse";
import { findMappingByTaxFieldCode, RecogniserMapping, RecogniserSingleMapping, TaxGuruTaxRateFieldCode } from "../recogniserFieldMapping";
import { TaxDocumentDto, TaxRateItem } from "../../../backend/taxguru";
import React from "react";
import { Field } from "../../../backend/taxguru/models/Field";
import { dateForRecogniser, isDateObjectValid } from "../../../utils/dateObjectUtils";
import dayjs from "dayjs";
import { isRecogniserCorrectionUpdateSupported } from "./documentUtils";
import type = TaxDocumentDto.type;

const MOSTLY_USED_DOCUMENT_DATE_FORMATS = [
    // order matters
    "DDMMM'YY",
    "MMMDD'YY",
    "YYYY-MM-DD",
    "YYYY-M-D",
    "DD/MM/YYYY",
    "DD-MM-YYYY",
    "DD.MM.YYYY",
    "D-M-YYYY",
    "D.M.YYYY",
    "DD.MM.YY",
    "DD-MM-YY",
    "D.M.YY",
    "D-M-YY",
];
const REGEX_SEARCH_PATTERNS_FOR_DATES = [
    /\d{1,2}-\d{1,2}-\d{2,4}/g, // DD-MM-YYYY
    /\d{1,2}-\d{1,2}-\d{2}/g, // DD-MM-YY
    /\d{1,2}\.\s*\d{1,2}\.\s*\d{2,4}/g, // DD.MM.YYYY
    /\d{1,2}\.\s*\d{1,2}\.\s*\d{2}/g, // DD.MM.YY
    /\d{1,2}\w{3}'\d{2}/g, // DDMMM'YY
    /\w{3}\d{1,2}'\d{2}/g, // MMMDD'YY
];
type ValueFromOcrType = number | dayjs.Dayjs | string | null | undefined;

export function areOcrFieldsEqual(field1: OcrField | undefined, field2: OcrField | undefined, ignoreSpecific = false): boolean {
    if (field1 === undefined || field2 === undefined) {
        return false;
    }
    const field1Copy: OcrField = { ...field1, ...(ignoreSpecific && { fieldCode: undefined, taxrateIndex: undefined }) };
    const field2Copy: OcrField = { ...field2, ...(ignoreSpecific && { fieldCode: undefined, taxrateIndex: undefined }) };
    return _.isEqual(field1Copy, field2Copy);
}

export function areFieldCodesEqual(fieldCode1: string | undefined | null, fieldCode2: string | undefined | null) {
    return isValueDefined(fieldCode1) && isValueDefined(fieldCode2) && fieldCode1 === fieldCode2;
}

function tryParseDateFromRecogniserOCR(str: string) {
    const convertedDateObject = dayjs(str?.trim(), MOSTLY_USED_DOCUMENT_DATE_FORMATS, true);
    if (convertedDateObject.isValid()) {
        return convertedDateObject;
    } else {
        for (const pattern of REGEX_SEARCH_PATTERNS_FOR_DATES) {
            const matches = str.match(pattern);
            for (const match of matches ?? []) {
                const date = dayjs(match, MOSTLY_USED_DOCUMENT_DATE_FORMATS);
                if (date.isValid()) {
                    return date;
                }
            }
        }
        return null;
    }
}

function tryParseNumberFromRecogniserOCR(str: string) {
    const keepOnlyLastSeparatorRegex = /[., ](?!\d*$)/g;
    const keepOnlyNumbersAndSeparator = /([^\d,.\-+]+)/g;
    const removeDotIfThousandsSeparator = /\.(?=\d{3,})/g;
    const possibleNumber = str
        .trim()
        .replaceAll(keepOnlyNumbersAndSeparator, "")
        .replaceAll(keepOnlyLastSeparatorRegex, "")
        .replace(",", ".")
        .replaceAll(removeDotIfThousandsSeparator, "");
    const finalFloat = parseFloat(possibleNumber);
    console.debug(`OCR number converted from string ${str}, after clean ${possibleNumber} and final float value of ${finalFloat}`);
    if (Number.isNaN(finalFloat)) {
        return ""; // prevent resetting to default
    }
    return finalFloat;
}

export function parseOcrValueToFormValue(str: string, taxFieldCode: FieldPath<DocumentDetailFormTypes>) {
    const targetIsNumeric = documentNumberFields.has(taxFieldCode);
    const targetIsDate = documentDateObjectFields.has(taxFieldCode);
    if (targetIsNumeric) {
        return tryParseNumberFromRecogniserOCR(str);
    } else if (targetIsDate) {
        return tryParseDateFromRecogniserOCR(str);
    } else if (taxFieldCode === "supplierCompanyIdNumber") {
        return str.replaceAll(/\D/g, "");
    } else {
        return str;
    }
}

function ocrValueToString(valueFromSelectedFields: ValueFromOcrType) {
    return isDateObjectValid(valueFromSelectedFields) ? dateForRecogniser(valueFromSelectedFields) : valueFromSelectedFields?.toString();
}

function createFieldListFromSelection(response: RecogniserResponse, mapping: RecogniserSingleMapping, valueFromSelectedFields: ValueFromOcrType) {
    if (!taxFieldCodeIsSuitableForFieldList(mapping.taxGuruFieldCode.taxFieldCode)) {
        return response.fieldList;
    }
    const matchingField = response?.fieldList?.filter((field) => field.code === mapping.recogniserFieldCode)?.[0];
    const fieldListsWithoutSelectedField = response?.fieldList?.filter((field) => field.code !== mapping.recogniserFieldCode) ?? [];
    const newFieldList = [...fieldListsWithoutSelectedField];
    if (valueFromSelectedFields) {
        newFieldList.push({
            ...matchingField,
            code: mapping.recogniserFieldCode,
            value: ocrValueToString(valueFromSelectedFields),
        });
    }
    return newFieldList;
}

function createOcrListFromSelection(response: RecogniserResponse, ocrFieldsSelected: Array<OcrField>, mapping: RecogniserSingleMapping) {
    const matchingFields: Array<OcrField> = [];
    const otherFields: Array<OcrField> = [];
    for (const ocrField of response?.ocrList ?? []) {
        const matching = ocrFieldsSelected.find((selected) => {
            return areOcrFieldsEqual(selected, ocrField, true);
        });
        if (matching) {
            // field match exactly
            matchingFields.push({
                ...ocrField,
                fieldCode: mapping.recogniserFieldCode,
                ...mapping.taxGuruFieldCode.ocrFieldUpdateObject,
            });
        } else if (ocrField.fieldCode === mapping.recogniserFieldCode && mapping.taxGuruFieldCode.matches(ocrField)) {
            // field does not match but belongs to previously recognized field
            otherFields.push({ ...ocrField, fieldCode: undefined });
        } else {
            otherFields.push(ocrField);
        }
    }
    return [...matchingFields, ...otherFields];
}

function createTaxRateListFromSelection(
    taxRateList: Array<TaxRateItem>,
    mapping: RecogniserSingleMapping,
    valueFromSelectedFields: number | dayjs.Dayjs | string | null | undefined,
    pages?: number[]
): Array<TaxRateItem> {
    const fieldRelatesToVat = documentTaxFields.has(mapping.taxGuruFieldCode.taxFieldCode);
    const fieldMappingClass = mapping.taxGuruFieldCode;
    const mappingRelatesToVat = fieldMappingClass instanceof TaxGuruTaxRateFieldCode;
    if (!fieldRelatesToVat || !mappingRelatesToVat) {
        return taxRateList;
    }
    const newTaxRateList: Array<TaxRateItem> = [];
    let match = false;
    for (const taxRateItem of taxRateList) {
        if (taxRateItem.taxrateIndex === fieldMappingClass.taxRateIndex) {
            const newTaxRateItemList =
                taxRateItem.taxrateFieldList?.filter((item) => [mapping.recogniserFieldCode, "TaxRatePercentage"].indexOf(item.code ?? "") < 0) ?? [];
            newTaxRateItemList.push(
                {
                    code: mapping.recogniserFieldCode,
                    value: ocrValueToString(valueFromSelectedFields),
                },
                { code: "TaxRatePercentage", value: fieldMappingClass.taxRateIndex.toString() }
            );
            const newTaxRateItem: TaxRateItem = {
                ...taxRateItem,
                taxrateFieldList: newTaxRateItemList ?? [],
            };
            newTaxRateList.push(newTaxRateItem);
            match = true;
        } else {
            newTaxRateList.push(taxRateItem);
        }
    }
    if (!match) {
        (pages ?? [undefined]).forEach((page) => {
            newTaxRateList.push({
                taxrateIndex: fieldMappingClass.taxRateIndex,
                page,
                taxrateFieldList: [
                    {
                        code: mapping.recogniserFieldCode,
                        value: ocrValueToString(valueFromSelectedFields),
                    },
                ],
            });
        });
    }
    return newTaxRateList;
}

/**
 * Returns undefined if taxDocument state forbids recogniser learning
 * @param taxDocument
 * @param formData
 * @param recogniserLearningResponse
 * @param mappings
 */
export function createRecogniserCorrectionOnSubmit(
    taxDocument: TaxDocumentDto,
    formData: DocumentDetailFormTypes,
    recogniserLearningResponse: RecogniserResponse,
    mappings: Array<RecogniserMapping>
): RecogniserResponse | undefined {
    if (isRecogniserCorrectionUpdateSupported(taxDocument)) {
        console.debug("Learning recogniser before submit...");
        const { fieldList, taxRateList } = createRecogniserListsFromForm(formData, recogniserLearningResponse, mappings);
        const recogniserCorrection = {
            ...recogniserLearningResponse,
            fieldList: fieldList,
            taxrateList: taxRateList,
        };
        if (formData.type && formData.type !== "OTHER") {
            recogniserCorrection["documentTypeId"] = resolveDocumentTypeId(formData.type);
        }
        return recogniserCorrection;
    }
    return recogniserLearningResponse;
}

function resolveDocumentTypeId(type: type) {
    switch (type) {
        case TaxDocumentDto.type.CONFIRMATION:
            return "CON";
        case TaxDocumentDto.type.INVOICE:
            return "INV";
        case TaxDocumentDto.type.RECEIPT:
            return "SIM";
        case TaxDocumentDto.type.OTHER:
            return undefined;
    }
}

export function learnRecogniserWithSelectedFields(
    valueFromSelectedFields: undefined | number | dayjs.Dayjs | null | string,
    ocrFieldsSelected: Array<OcrField>,
    updateResponse: React.Dispatch<React.SetStateAction<RecogniserResponse>>,
    mapping: RecogniserSingleMapping | undefined
) {
    if (!mapping) {
        return;
    }

    updateResponse((response) => {
        // replacing ocr fields
        const newOcrFieldList = createOcrListFromSelection(response, ocrFieldsSelected, mapping);
        // replacing field list
        const newFieldList = createFieldListFromSelection(response, mapping, valueFromSelectedFields);
        const pages = ocrFieldsSelected.map((value) => value.page ?? 0);
        const newTaxRateList = createTaxRateListFromSelection(response.taxrateList ?? [], mapping, valueFromSelectedFields, pages);
        return {
            ...response,
            ocrList: newOcrFieldList,
            fieldList: newFieldList,
            taxrateList: newTaxRateList,
        };
    });
}

function extractValueOfObjectTypeForRecogniser(
    field: keyof DocumentDetailFormTypes,
    objectValue:
        | TaxDocumentDto["owner"]
        | TaxDocumentDto["supplier"]
        | TaxDocumentDto["supplierPerson"]
        | TaxDocumentDto["currency"]
        | undefined
        | null
): string | undefined {
    if (field === "owner") {
        return getPersonName((objectValue as TaxDocumentDto["owner"])?.accountant?.user?.person);
    } else if (field === "supplier") {
        return (objectValue as TaxDocumentDto["supplier"])?.idNumber;
    } else if (field === "supplierPerson") {
        return getPersonName(objectValue as TaxDocumentDto["supplierPerson"]);
    } else if (field === "currency") {
        return (objectValue as TaxDocumentDto["currency"])?.code;
    } else {
        throw new Error("Cannot convert field " + field + " for recogniser");
    }
}

export function paymentMeanToNumber(data: DocumentDetailFormTypes) {
    switch (data.paymentMeans) {
        case TaxDocumentDto.paymentMeans.CASH_PAYMENT:
            return 10;
        case TaxDocumentDto.paymentMeans.CHEQUE_PAYMENT:
            return 20;
        case TaxDocumentDto.paymentMeans.CREDIT_TRANSFER:
            return 31;
        case TaxDocumentDto.paymentMeans.MONEY_TRANSFER_TO_ACCOUNT:
            return 42;
        case TaxDocumentDto.paymentMeans.CARD_PAYMENT:
            return 48;
        case TaxDocumentDto.paymentMeans.DIRECT_DEBIT:
            return 49;
        case TaxDocumentDto.paymentMeans.PAYMENT_BY_POSTGIRO:
            return 50;
        case TaxDocumentDto.paymentMeans.COMPOSITION_BETWEEN_PARTNERS:
            return 97;
        default:
            return 0;
    }
}

function convertFormValueForRecogniser(
    value: DocumentDetailFormTypes[keyof DocumentDetailFormTypes],
    formPropAsFormType: keyof DocumentDetailFormTypes
): string | undefined {
    if (value === null || value === undefined) {
        return undefined;
    }
    if (isDateObjectValid(value)) {
        return dateForRecogniser(value);
    } else if (typeof value === "number" || typeof value === "string" || typeof value === "boolean") {
        return value.toString();
    } else if (documentObjectFields.has(formPropAsFormType)) {
        return extractValueOfObjectTypeForRecogniser(formPropAsFormType, value);
    } else {
        return value.toString();
    }
}

function createFieldListItemFromForm(
    propName: keyof DocumentDetailFormTypes,
    formValues: DocumentDetailFormTypes,
    fieldListGenerated: Array<Field>,
    mappings: Array<RecogniserMapping>
) {
    const mapping = findMappingByTaxFieldCode(mappings, propName);
    if (mapping && taxFieldCodeIsSuitableForFieldList(mapping.taxGuruFieldCode.taxFieldCode)) {
        let value = formValues[propName];
        if (mapping.taxGuruFieldCode.valueFromForm) {
            value = mapping.taxGuruFieldCode.valueFromForm(formValues);
        }
        const convertedValue = convertFormValueForRecogniser(value, propName);
        if (isValueDefined(convertedValue)) {
            const recogniserFieldCode = mapping.recogniserFieldCode;
            const matchingField = fieldListGenerated.filter((item) => item.code === recogniserFieldCode)?.[0];
            const fieldListFiltered = fieldListGenerated.filter((item) => item.code !== recogniserFieldCode);
            fieldListFiltered.push({ ...matchingField, code: recogniserFieldCode, value: convertedValue });
            return fieldListFiltered;
        }
    }
    return fieldListGenerated;
}

function createTaxRateListFromForm(
    propName: keyof DocumentDetailFormTypes,
    formValues: DocumentDetailFormTypes,
    existingTaxRateList: Array<TaxRateItem>,
    mappings: Array<RecogniserMapping>
): Array<TaxRateItem> {
    const mapping = findMappingByTaxFieldCode(mappings, propName);
    if (mapping) {
        let value;
        if (mapping.taxGuruFieldCode instanceof TaxGuruTaxRateFieldCode && mapping.taxGuruFieldCode.valueFromForm) {
            value = mapping.taxGuruFieldCode.valueFromForm(formValues);
        } else {
            value = formValues[propName];
        }
        const convertedValue = convertFormValueForRecogniser(value, propName);
        if (isValueDefined(convertedValue)) {
            return createTaxRateListFromSelection(existingTaxRateList, mapping, convertedValue);
        }
    }
    return existingTaxRateList;
}

function createRecogniserListsFromForm(
    formValues: DocumentDetailFormTypes,
    recogniserLearningResponse: RecogniserResponse,
    mappings: Array<RecogniserMapping>
): { fieldList: Array<Field>; taxRateList: Array<TaxRateItem> } {
    let fieldList: Array<Field> = recogniserLearningResponse.fieldList ?? [];
    let taxRateList: Array<TaxRateItem> = recogniserLearningResponse.taxrateList ?? [];

    for (const formProp in formValues) {
        const formPropAsFormType = formProp as keyof DocumentDetailFormTypes;
        if (documentTaxFields.has(formPropAsFormType)) {
            taxRateList = createTaxRateListFromForm(formPropAsFormType, formValues, taxRateList, mappings);
            // tax rate fields
        } else {
            // other fields
            fieldList = createFieldListItemFromForm(formPropAsFormType, formValues, fieldList, mappings);
        }
    }
    return { fieldList, taxRateList };
}

export function isOcrFieldEqualToSelectedFormMapping(ocrField: OcrField, recogniserSingleMapping: RecogniserSingleMapping) {
    return (
        areFieldCodesEqual(ocrField.fieldCode, recogniserSingleMapping.recogniserFieldCode) &&
        recogniserSingleMapping.taxGuruFieldCode.matches(ocrField)
    );
}

function taxFieldCodeIsSuitableForFieldList(taxFieldCode: Path<DocumentDetailFormTypes>): boolean {
    return !documentTaxFields.has(taxFieldCode) || taxFieldCode === "amountTotal"; // since amountTotal is also tax
}

export function isModifiedByTax(response: RecogniserResponse, mapping: RecogniserSingleMapping): boolean {
    if (mapping?.taxGuruFieldCode instanceof TaxGuruTaxRateFieldCode) {
        const { taxRateIndex } = mapping.taxGuruFieldCode;
        return (response?.taxrateList ?? [])
            .filter((field) => field.taxrateIndex === taxRateIndex)
            .flatMap((field) => field.taxrateFieldList)
            .filter((itemField) => itemField?.code === mapping.recogniserFieldCode)
            .some((itemField) => itemField?.modifiedByDokladovna);
    }
    return (response?.fieldList ?? [])
        .filter((field) => field.code === mapping.recogniserFieldCode)
        .some((field) => field.modifiedByDokladovna === true);
}
